/**
 * Seitenleiste für Formulare, die neben ihrem Kontext stehen sollen — etwa eine Kanalregel neben
 * der Zeitleiste. Darunter liegt derselbe Radix-Dialog wie bei `Dialog`: Fokus-Trap, Escape,
 * Focus-Return und sichtbarer Schließen-Knopf (FRONTEND_GUIDELINES §8). Kopf und Fuß stehen fest,
 * nur der Inhalt scrollt; unter 768 px füllt die Leiste den Bildschirm.
 */
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';

export const Sheet = RadixDialog.Root;
export const SheetTitle = RadixDialog.Title;
export const SheetDescription = RadixDialog.Description;

export const SheetContent = forwardRef<HTMLDivElement, RadixDialog.DialogContentProps>(
  ({ className, children, ...props }, ref) => {
    const { t } = useTranslation();
    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="bg-ink/30 fixed inset-0 z-50" />
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'bg-paper border-line/60 text-ink shadow-card fixed inset-y-0 right-0 z-50 flex h-dvh w-full flex-col border-l md:max-w-[480px]',
            className,
          )}
          {...props}
        >
          {children}
          <RadixDialog.Close
            className="text-ink-soft hover:text-ink absolute right-3 top-3 grid min-h-11 min-w-11 place-items-center rounded-md transition-colors"
            aria-label={t('common.close')}
          >
            <X size={18} strokeWidth={2} />
          </RadixDialog.Close>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);
SheetContent.displayName = 'SheetContent';

export function SheetHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('shrink-0 px-6 pb-2 pr-14 pt-6', className)} {...props} />;
}

/** Der einzige scrollende Bereich; `min-h-0` hält die Spalte davon ab, stattdessen zu wachsen. */
export function SheetBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', className)} {...props} />;
}

export function SheetFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-line/60 bg-card flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
      {...props}
    />
  );
}
